import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'

interface SubmitPayload<TParsedData> {
  name: string
  data: TParsedData
}

interface Props<TParsedData> {
  isOpen: boolean
  menuName: string
  nameField: string
  existingNames?: Array<string | number>
  onClose: () => void
  onDownloadSample: () => void
  parseFile: (file: File) => Promise<TParsedData>
  onSubmit: (payload: SubmitPayload<TParsedData>) => Promise<void> | void
}

const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv']

const DataUploadPopup = <TParsedData,>({
  isOpen,
  menuName,
  nameField,
  existingNames = [],
  onClose,
  onDownloadSample,
  parseFile,
  onSubmit,
}: Props<TParsedData>) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [name, setName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle')

  useEffect(() => {
    if (!isOpen) {
      setName('')
      setFile(null)
      setErrorMessage('')
      setStatus('idle')
    }
  }, [isOpen])

  if (!isOpen) return null

  const isLoading = status === 'loading'

  const validateFileExtension = (targetFile: File) => {
    const extension = targetFile.name.split('.').pop()?.toLowerCase()
    return extension ? ACCEPTED_EXTENSIONS.includes(extension) : false
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null
    setErrorMessage('')

    if (!selectedFile) {
      setFile(null)
      return
    }

    if (!validateFileExtension(selectedFile)) {
      setFile(null)
      setErrorMessage('.xlsx, .xls, .csv 파일만 등록할 수 있습니다.')
      event.target.value = ''
      return
    }

    setFile(selectedFile)
  }

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    const hasDuplicateName = existingNames.some(
      (item) => String(item).trim() === trimmedName
    )

    if (!trimmedName) {
      setErrorMessage(`${nameField}을 입력해주세요.`)
      return
    }

    if (hasDuplicateName) {
      setErrorMessage(`이미 등록된 ${nameField}입니다.`)
      return
    }

    if (!file) {
      setErrorMessage('등록할 파일을 선택해주세요.')
      return
    }

    try {
      setStatus('loading')
      setErrorMessage('')
      const parsedData = await parseFile(file)
      await onSubmit({ name: trimmedName, data: parsedData })
      setStatus('success')

      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (error) {
      console.error(error)
      setStatus('idle')
      setErrorMessage(
        error instanceof Error
          ? error.message
          : '파일 등록 중 오류가 발생했습니다.'
      )
    }
  }

  const handleClose = () => {
    if (!isLoading) onClose()
  }

  return (
    <Overlay onClick={handleClose}>
      <Wrapper onClick={(event) => event.stopPropagation()}>
        {status === 'loading' && (
          <StatusContent>
            <Spinner />
            <StatusText>데이터를 저장 중입니다...</StatusText>
          </StatusContent>
        )}

        {status === 'success' && (
          <StatusContent>
            <CheckIcon viewBox="0 0 24 24">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
            </CheckIcon>
            <StatusText>등록이 완료되었습니다.</StatusText>
          </StatusContent>
        )}

        {status === 'idle' && (
          <>
            <Header>
              <Title>{menuName} 데이터 등록</Title>
              <CloseButton type="button" onClick={handleClose}>
                x
              </CloseButton>
            </Header>
            <Body>
              <Field>
                <Label>{nameField}</Label>
                <TextInput
                  value={name}
                  placeholder={`${nameField} 입력`}
                  onChange={(event) => {
                    setName(event.target.value)
                    setErrorMessage('')
                  }}
                />
              </Field>

              <Field>
                <Label>파일</Label>
                <FileRow>
                  <FileName>{file?.name || '선택된 파일 없음'}</FileName>
                  <FileButton
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    파일 선택
                  </FileButton>
                </FileRow>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
              </Field>

              <SampleButton type="button" onClick={onDownloadSample}>
                예시 엑셀 다운로드
              </SampleButton>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </Body>
            <ButtonGroup>
              <CancelButton type="button" onClick={handleClose}>
                취소
              </CancelButton>
              <SubmitButton type="button" onClick={handleSubmit}>
                등록
              </SubmitButton>
            </ButtonGroup>
          </>
        )}
      </Wrapper>
    </Overlay>
  )
}

export default DataUploadPopup

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  display: flex;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 420px;
  min-height: 260px;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0px 0px 24px rgba(0, 0, 0, 0.16);
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #373a40;
`

const Title = styled.div`
  font-family: SUIT;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: #ffffff;
`

const CloseButton = styled.button`
  display: flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #ffffff;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-family: SUIT;
  font-size: 13px;
  font-weight: 600;
  line-height: 16px;
  color: #333333;
`

const TextInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #333333;
  font-family: SUIT;
  font-size: 14px;

  &:focus {
    border-color: #57c9a7;
    outline: none;
  }
`

const FileRow = styled.div`
  display: flex;
  gap: 8px;
`

const FileName = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  height: 40px;
  align-items: center;
  overflow: hidden;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #777777;
  font-family: SUIT;
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FileButton = styled.button`
  flex: 0 0 auto;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #b3b3b3;
  border-radius: 4px;
  background: #ffffff;
  color: #4d4d4d;
  cursor: pointer;
  font-family: SUIT;
  font-size: 13px;
  font-weight: 500;
`

const HiddenFileInput = styled.input`
  display: none;
`

const SampleButton = styled.button`
  height: 36px;
  border: 1px solid #57c9a7;
  border-radius: 4px;
  background: #ffffff;
  color: #319d7d;
  cursor: pointer;
  font-family: SUIT;
  font-size: 13px;
  font-weight: 600;
`

const ErrorMessage = styled.div`
  color: #f36464;
  font-family: SUIT;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 20px 20px;
`

const BaseButton = styled.button`
  display: flex;
  width: 96px;
  height: 40px;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: SUIT;
  font-size: 14px;
  font-weight: 600;
`

const CancelButton = styled(BaseButton)`
  background: #f5f5f5;
  color: #4d4d4d;
`

const SubmitButton = styled(BaseButton)`
  background: #e3f2fd;
  color: #2196f3;
`

const StatusContent = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`

const StatusText = styled.div`
  font-family: SUIT;
  font-size: 17px;
  font-weight: 600;
  color: #333333;
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border: 4px solid #e3f2fd;
  border-top-color: #2196f3;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`

const CheckIcon = styled.svg`
  width: 44px;
  height: 44px;
  fill: #57c9a7;
`
